import React, {useState} from 'react';
import {ActivityIndicator, Platform, StyleSheet} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import Animated, {FadeIn} from 'react-native-reanimated';
import {observer} from 'mobx-react-lite';

import NoTillPaymentServiceAvailableModal from '../../../components/modals/NoPaymentConfiguredModal';
import CardTokeniserTillModal from '../../../components/payments/till/cardTokeniser/CardTokeniserTillModal';
import PaymentMethodSelector from '../../../components/payments/till/PaymentMethodSelector';
import type {PaymentMethodType} from '../../../components/payments/till/PaymentMethodSelector/types';
import SafeAreaPadding from '../../../components/SafeAreaPadding';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import {useTabBarBottomPadding} from '../../../hooks/useTabBarBottomPadding';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {PAYMENT_SERVICE_TYPES, usePaymentServices} from '../../../queries/paymentCard';
import {useStores} from '../../../stores/use-stores';
import {CheckoutActionButton} from '../components/CheckoutActionButton';
import ConfirmOrderPlacementModal from '../components/ConfirmOrderPlacementModal';
import CouponBlockedNotice from '../components/CouponBlockedNotice';
import GiftCardsSection from '../components/GiftCardsSection';
import OrderTotals from '../components/OrderTotals';
import PickupDeliveryDetails from '../components/PickupDeliveryDetails';
import ThreeDsMobileBrowserDataIframe from '../components/ThreeDsMobileBrowserDataIframe';
import {
  createApplePayCheckoutConfigs,
  createGooglePayCheckoutConfigs,
  useCardManagement,
  useCheckoutSession,
  useOrderSubmission,
  usePaymentValidation,
} from '../hooks';
import UnifiedCheckoutScreen from '../UnifiedCheckout';
import useFetchMobileWebView3dsBrowserDataJSFocusEffect from '../useFetchMobileWebView3dsBrowserDataJSFocusEffect';
import {handleContinue} from '../util/validation';

const CheckoutPaymentScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.CART_ORDER_CHECKOUT_PAYMENT>) => {
    // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
    // React requires hooks to be called in the same order on every render

    // Session management: empty cart redirect (no login reload needed on payment screen)
    useCheckoutSession({step: 'payment', skipLoginReload: true});

    const {cartStore, userStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();
    const tabBarPadding = useTabBarBottomPadding();

    const {mobileWebView3DsBrowserDataJS, browserData, setBrowserData} =
      useFetchMobileWebView3dsBrowserDataJSFocusEffect();

    // Payment method selection state
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodType>('card');
    const [canPay, setCanPay] = useState(true); // Whether payment can proceed (false when card selected but no card saved)

    // Card management from consolidated hook
    const {
      paymentCard,
      isCardActionLoading,
      handleAddOrChangeCard,
      handleDeleteCard,
      isCardTokeniserModalVisible,
      setCardTokeniserModalVisible,
      isNoPaymentServiceModalVisible,
      setNoPaymentServiceModalVisible,
    } = useCardManagement();

    // Get Apple Pay service ID from payment services
    const {data: paymentServicesData} = usePaymentServices();
    const applePayServiceId =
      paymentServicesData?.services.find(s => s.type === PAYMENT_SERVICE_TYPES.APPLE_PAY)?.id ??
      null;
    const googlePayServiceId =
      paymentServicesData?.services.find(s => s.type === PAYMENT_SERVICE_TYPES.GOOGLE_PAY)?.id ??
      null;

    // Apple Pay configs
    const {applePayConfig, applePayIOSConfig} = createApplePayCheckoutConfigs({
      cartStore,
      applePayServiceId,
      logTag: 'CheckoutPaymentScreen',
    });

    // Google Pay configs (Android native + web)
    const {googlePayConfig, googlePayWebConfig} = createGooglePayCheckoutConfigs({
      cartStore,
      googlePayServiceId,
      logTag: 'CheckoutPaymentScreen',
    });

    // Payment validation with billingAddress check
    const {validateWithToast} = usePaymentValidation({
      cartStore,
      userStore,
      paymentCard,
      selectedPaymentMethod,
    });

    // Order submission - consolidated logic for validation, modal, and API call
    const {handlePlaceOrder, submitOrder, isConfirmModalVisible, closeConfirmModal} =
      useOrderSubmission({
        cartStore,
        browserData,
        paymentCardId: paymentCard?.id,
        validateOrder: validateWithToast,
      });

    // DIS-643: Server validation happens here BEFORE showing confirmation modal
    const validatedPaymentPress = handleContinue({
      callback: handlePlaceOrder,
      cartStore,
      userStore,
    });

    // Web desktop uses unified checkout layout
    if (isWebDesktopScreen) {
      return <UnifiedCheckoutScreen step="payment" navigation={navigation} />;
    }

    // Mobile uses existing implementation below

    if (Platform.OS !== 'web' && !mobileWebView3DsBrowserDataJS) {
      return (
        <ContentView testID="checkout-screen">
          <ActivityIndicator />
        </ContentView>
      );
    }

    return (
      <ContentView testID="checkout-screen">
        <KeyboardAwareScrollView
          style={styles.scrollView}
          keyboardShouldPersistTaps="always"
          showsVerticalScrollIndicator={isWebDesktopScreen}
          showsHorizontalScrollIndicator={isWebDesktopScreen}
          contentContainerStyle={[styles.scrollContent, {paddingBottom: tabBarPadding}]}
          enableOnAndroid
          extraScrollHeight={40}>
          <>
            <Animated.View entering={FadeIn}>
              <PaymentMethodSelector
                selectedMethod={selectedPaymentMethod}
                onMethodChange={setSelectedPaymentMethod}
                onAddCard={handleAddOrChangeCard}
                onChangeCard={handleAddOrChangeCard}
                onDeleteCard={handleDeleteCard}
                isCardActionLoading={isCardActionLoading}
                applePayConfig={applePayConfig}
                onCanPayChange={setCanPay}
              />
            </Animated.View>

            <GiftCardsSection testID="checkout-payment-gift-cards" />
            <PickupDeliveryDetails />
            <OrderTotals>
              <CheckoutActionButton
                selectedPaymentMethod={selectedPaymentMethod}
                applePayConfig={applePayConfig}
                applePayIOSConfig={applePayIOSConfig}
                googlePayConfig={googlePayConfig}
                googlePayWebConfig={googlePayWebConfig}
                onPayNow={validatedPaymentPress}
                isLoading={cartStore.submittingOrder || cartStore.isLoadingOrder}
                isDisabled={
                  cartStore.submittingOrder ||
                  cartStore.isLoadingOrder ||
                  !canPay ||
                  cartStore.couponBlocksCheckout
                }
                testID="checkout-payment-screen-place-order-button"
              />
              <CouponBlockedNotice />
            </OrderTotals>
          </>
        </KeyboardAwareScrollView>
        <ConfirmOrderPlacementModal
          isVisible={isConfirmModalVisible}
          onClose={closeConfirmModal}
          onSubmit={submitOrder}
          isSubmitting={cartStore.submittingOrder}
        />
        <ThreeDsMobileBrowserDataIframe
          mobileWebView3DsBrowserDataJS={mobileWebView3DsBrowserDataJS}
          onBrowserData={setBrowserData}
        />
        <CardTokeniserTillModal
          isVisible={isCardTokeniserModalVisible}
          onCancel={() => setCardTokeniserModalVisible(false)}
          navigationTarget={AppRoute.CART_ORDER_CHECKOUT_PAYMENT}
        />
        <NoTillPaymentServiceAvailableModal
          onOK={() => setNoPaymentServiceModalVisible(false)}
          isVisible={isNoPaymentServiceModalVisible}
        />
        <SafeAreaPadding bottom />
      </ContentView>
    );
  },
);

export default CheckoutPaymentScreen;

const styles = StyleSheet.create({
  scrollView: {
    backgroundColor: 'white',
  },
  scrollContent: {
    gap: 16,
  },
});
